import { useState } from 'react'

import BookOpenIcon from '@heroicons/react/outline/BookOpenIcon'
import ChatAltIcon from '@heroicons/react/outline/ChatAltIcon'
import FireIcon from '@heroicons/react/outline/FireIcon'
import StarIcon from '@heroicons/react/outline/StarIcon'
import { useIntercom } from 'react-use-intercom'

import { PlanName } from '@tribeplatform/gql-client/types'
import {
  I18nShape,
  T,
  useStaticIntl,
} from '@tribeplatform/react-components/i18n'
import { useNetwork } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { SpinnerIcon } from '@tribeplatform/react-ui-kit/icons'
import { Modal } from '@tribeplatform/react-ui-kit/Modal'

import { RuntimeConfigs } from '../../config'
import { useSupportCommunitySso } from '../../hooks/useSupportCommunitySso'

const itemsGenerator = (intl: I18nShape) => [
  {
    title: intl.$t({
      defaultMessage: 'Knowledge Base',
      id: 'RESERVED.Support.KB',
    }),
    description: intl.$t({
      defaultMessage: 'Access how-to articles and best practices',
      id: 'RESERVED.Support.KB.Description[2]',
    }),
    icon: BookOpenIcon,
    link: '/knowledge-base',
  },
  {
    title: intl.$t({
      defaultMessage: 'Support',
      id: 'RESERVED.Support.Support',
    }),
    description: intl.$t({
      defaultMessage:
        'Find answers, ask questions, and help others in our community',
      id: 'RESERVED.Support.AskForHelp.Description',
    }),
    icon: FireIcon,
    link: '/community/ask-for-help',
  },
  {
    title: intl.$t({
      defaultMessage: 'Product Updates',
      id: 'RESERVED.Support.ProductUpdates',
    }),
    description: intl.$t({
      defaultMessage: 'Stay up to date with {BRAND} features and improvements',
      id: 'RESERVED.Support.ProductUpdates.Description',
    }),
    icon: StarIcon,
    link: '/product-updates',
  },
  {
    title: intl.$t({
      defaultMessage: 'Wishlist',
      id: 'RESERVED.Support.WishList',
    }),
    description: intl.$t({
      defaultMessage:
        'Share feature requests with the community and {BRAND} team',
      id: 'RESERVED.Support.WishList.Description',
    }),
    icon: ChatAltIcon,
    link: '/community/wishlist',
  },
]
export const SupportModal = ({ open, onClose }) => {
  const items = useStaticIntl(itemsGenerator)
  const { data: network } = useNetwork()
  const { getSupportCommunityUrl } = useSupportCommunitySso()
  const [supportCommunityLoading, setSupportCommunityLoading] = useState(null)
  const planName = network?.subscriptionPlan?.name ?? ''
  const showDirectChat =
    planName !== PlanName.plus &&
    planName !== PlanName.Plus &&
    planName !== PlanName.PlusLegacy &&
    planName !== PlanName.premium &&
    planName !== PlanName.Premium &&
    planName !== PlanName.PremiumLegacy

  const { show } = useIntercom()

  const openChat = () => {
    if (typeof window !== 'undefined') {
      if ((window as any).Intercom) {
        show()
      } else {
        window.open('mailto:success@bettermode.com', '_blank')
      }
    }
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Header
        title={<T defaultMessage="Help & Community" id="RESERVED.Support" />}
      />
      <Modal.Content>
        <ul className="flex flex-col space-y-1">
          {items.map(item => {
            if (!item) {
              return null
            }
            const Icon = item.icon
            if (
              planName === PlanName.Basic ||
              planName === PlanName.basic ||
              network?.subscriptionPlan?.trial
            ) {
              return (
                <li key={item.title}>
                  <a
                    href={`${RuntimeConfigs?.SUPPORT_COMMUNITY_URL}${item.link}`}
                    className="flex space-s-3 hover:bg-surface-hovered rounded-md p-3"
                  >
                    <Icon className="w-5 h-5 flex-shrink-0 mt-0.5 text-action-primary" />
                    <div>
                      <h3 className="text-base font-medium">{item.title}</h3>
                      <div className="text-sm text-content-subdued">
                        {item.description}
                      </div>
                    </div>
                  </a>
                </li>
              )
            }

            return (
              <li key={item.title}>
                <a
                  onClick={async e => {
                    e.preventDefault()
                    setSupportCommunityLoading(item.link)
                    const fallbackUrl = `${RuntimeConfigs?.SUPPORT_COMMUNITY_URL}${item.link}`
                    try {
                      const url = await getSupportCommunityUrl()
                      window.location.href = url
                        ? `${url}${
                            url?.indexOf('?') === -1 ? '?' : '&'
                          }redirect_uri=${item.link}`
                        : fallbackUrl
                    } catch (err) {
                      window.location.href = fallbackUrl
                    }
                  }}
                  href="#"
                  className="flex space-s-3 hover:bg-surface-hovered rounded-md p-3"
                >
                  {supportCommunityLoading === item.link ? (
                    <SpinnerIcon className="animate-spin w-5 h-5 flex-shrink-0 mt-0.5" />
                  ) : (
                    <Icon className="w-5 h-5 flex-shrink-0 mt-0.5 text-action-primary" />
                  )}
                  <div>
                    <h3 className="text-base font-medium">{item.title}</h3>
                    <div className="text-sm text-content-subdued">
                      {item.description}
                    </div>
                  </div>
                </a>
              </li>
            )
          })}
          {showDirectChat && (
            <>
              <li className="pt-3">
                <T
                  defaultMessage="<emphasis>Still can’t find what you’re looking for?</emphasis><basic>Let us know how we can help. Send our Support Team a message.</basic>"
                  id="RESERVED.Support.Message.Description"
                  values={{
                    emphasis: chunks => (
                      <h3 className="text-base font-medium">{chunks}</h3>
                    ),
                    basic: chunks => (
                      <div className="text-sm text-content-subdued">
                        {chunks}
                      </div>
                    ),
                  }}
                />
              </li>
              <li className="pt-3">
                <Button onClick={openChat}>
                  <T
                    defaultMessage="Message us"
                    id="RESERVED.Support.Message"
                  />
                </Button>
              </li>
            </>
          )}
        </ul>
      </Modal.Content>
    </Modal>
  )
}
